'use client';

import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';
import { CommandPaletteContext } from './command-palette-context';
import { PaletteErrorBoundary } from './palette-error-boundary';
import type { ICommandPaletteContextValue } from './use-command-palette-context-types';
import { useKeybindings } from './use-keybindings';

const CommandPaletteRoot = dynamic(
  () => import('./command-palette-root').then((mod) => mod.CommandPaletteRoot),
  { ssr: false, loading: () => null },
);

interface ICommandPaletteProviderProps {
  children: React.ReactNode;
}

export function CommandPaletteProvider({
  children,
}: ICommandPaletteProviderProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);

  const openPalette = useCallback((q?: string) => {
    setInitialQuery(q ?? '');
    setHasOpenedOnce(true);
    setIsOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setIsOpen(false);
    setInitialQuery('');
  }, []);

  useKeybindings({ isOpen, openPalette });

  const value = useMemo<ICommandPaletteContextValue>(
    () => ({ isOpen, initialQuery, openPalette, closePalette }),
    [isOpen, initialQuery, openPalette, closePalette],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      {hasOpenedOnce && (
        <PaletteErrorBoundary>
          <CommandPaletteRoot />
        </PaletteErrorBoundary>
      )}
    </CommandPaletteContext.Provider>
  );
}
